import { useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from 'use-intl';
import {
  Body, Button, Caption, Heading, I, Dialog, DialogFooter, DialogSection, Row, Stack, Textarea,
} from '@leanin/ui';

const DESCRIPTION_MAX = 5000;

export interface ReportBugModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    description: string;
  }) => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function ReportBugModal({ open, onClose, onSubmit }: ReportBugModalProps) {
  const t = useTranslations('Feedback');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isPending, startTransition] = useTransition();
  // Set synchronously in `submit` — `isPending` alone leaves a window before
  // the pending render commits where an Escape would dismiss mid-flight,
  // wiping the description via the close-reset effect and landing the
  // result on a closed dialog.
  const submitInFlightRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setDescription('');
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const submit = () => {
    if (submitInFlightRef.current) return;
    setError(null);
    const trimmed = description.trim();
    if (trimmed.length === 0) {
      setError(t('reportBug.errorEmpty'));
      return;
    }
    submitInFlightRef.current = true;
    startTransition(async () => {
      try {
        const result = await onSubmit({ description: trimmed });
        if (!result.ok) {
          setError(result.message);
          return;
        }
        setSuccess(true);
      } finally {
        submitInFlightRef.current = false;
      }
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      canDismiss={() => !submitInFlightRef.current && !isPending}
      size="md"
      accessibleTitle={t('reportBug.title')}
    >
      <DialogSection bleed>
        <Row justify="space-between" align="center" gap="lg" py="lg" px="lg">
          <Heading level={3}>{t('reportBug.title')}</Heading>
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label={t('reportBug.closeAria')}
            onClick={onClose}
            disabled={isPending}
          />
        </Row>
      </DialogSection>

      <DialogSection scrollable divider>
        <Stack gap="lg">
          {success ? (
            <Body>{t('reportBug.successBody')}</Body>
          ) : (
            <>
              <Stack gap="sm">
                <Textarea
                  rows={6}
                  aria-label={t('reportBug.fieldLabel')}
                  value={description}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length <= DESCRIPTION_MAX) setDescription(v);
                  }}
                  placeholder={t('reportBug.placeholder')}
                />
                {description.length >= DESCRIPTION_MAX - 500 && (
                  <Row justify="flex-end" align="center">
                    <Caption>
                      {description.length} / {DESCRIPTION_MAX}
                    </Caption>
                  </Row>
                )}
              </Stack>
              <Caption>{t('reportBug.disclosure')}</Caption>

              {error !== null && (
                <span role="alert" aria-live="polite">
                  <Body color="destructive">
                    {error}
                  </Body>
                </span>
              )}
            </>
          )}
        </Stack>
      </DialogSection>

      <DialogFooter>
        <Row gap="md" align="center">
          {success ? (
            <Button variant="primary" onClick={onClose}>
              {t('reportBug.close')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={isPending}>
                {t('reportBug.cancel')}
              </Button>
              <Button
                variant="primary"
                iconRight={I.send}
                onClick={submit}
                loading={isPending}
              >
                {t('reportBug.submit')}
              </Button>
            </>
          )}
        </Row>
      </DialogFooter>
    </Dialog>
  );
}
